import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";


const CustomerModal = ({ customer, onClose }) => {
  if (!customer) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-semibold text-gray-800">Customer Details</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Profile Picture */}
            <div className="md:col-span-2 flex justify-center">
              {customer.profilepicture ? (
                <img
                  src={customer.profilepicture}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-3xl text-gray-500">
                    {customer.firstName?.[0]}{customer.lastName?.[0]}
                  </span>
                </div>
              )}
            </div>

            {/* Personal Information */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800 border-b pb-2">Personal Information</h4>
              <div className="space-y-2">
                <p><span className="font-medium">Name:</span> {`${customer.name}`}</p>
                <p><span className="font-medium">Email:</span> {customer.email}</p>
                <p><span className="font-medium">Phone:</span> {customer.phone || 'N/A'}</p>
                <p><span className="font-medium">Gender:</span> {customer.gender || 'N/A'}</p>
                <p><span className="font-medium">Birthday:</span> {customer.Birthday ? new Date(customer.Birthday).toLocaleDateString() : 'N/A'}</p>
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800 border-b pb-2">Address Information</h4>
              <div className="space-y-2">
                <p><span className="font-medium">Street:</span> {customer.address?.streetAddress || 'N/A'}</p>
                <p><span className="font-medium">City:</span> {customer.address?.city || 'N/A'}</p>
                <p><span className="font-medium">State:</span> {customer.address?.state || 'N/A'}</p>
                <p><span className="font-medium">Country:</span> {customer.address?.countryRegion || 'N/A'}</p>
                <p><span className="font-medium">ZIP Code:</span> {customer.address?.zipCode || 'N/A'}</p>
              </div>
            </div>

            {/* Account Information */}
            <div className="md:col-span-2 space-y-4">
              <h4 className="font-semibold text-gray-800 border-b pb-2">Account Information</h4>
              <div className="space-y-2">
                <p><span className="font-medium">Join Date:</span> {new Date(customer.createdAt).toLocaleDateString()}</p>
                <p><span className="font-medium">Status:</span> 
                  <span className={`ml-2 px-3 py-1 rounded-full text-xs font-medium ${
                    customer.status === "Active" ? "bg-green-100 text-green-800" :
                    customer.status === "Inactive" ? "bg-red-100 text-red-800" :
                    "bg-yellow-100 text-yellow-800"
                  }`}>
                    {customer.status}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const fetchCustomers = async (search = "") => {
    try {
      setIsSearching(true);
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(
        // `http://localhost:3005/admin/customers?search=${search}`
        `https://afric-fresh-backend.onrender.com/admin/customers?search=${search}`
        ,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCustomers(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Debounce search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm) {
        fetchCustomers(searchTerm);
      } else {
        fetchCustomers();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);


  const handleRowClick = (customer) => {
    setSelectedCustomer(customer);
  };


  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {selectedCustomer && (
        <CustomerModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-semibold text-gray-800">Customer List</h2>
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {isSearching && (
            <div className="absolute right-3 top-2.5">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <div className="bg-white shadow-lg rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-600 uppercase tracking-wider">
                  Join Date
                </th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No customers found
                  </td>
                </tr>
              ) : (
                customers.map((customer, index) => (
                  <tr
                    key={customer.id}
                    onClick={() => handleRowClick(customer)}
                    className={`${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    } hover:bg-gray-100`}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-800">
                      {customer.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {customer.email}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {customer.phone}
                    </td>
                    
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {customer.joinDate}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CustomerList;